import { reportLead as fbReportLead } from "./metaPixel";
import { reportPurchase as fbReportPurchase } from "./metaPixel";
import { reportSubscribe as fbReportSubscribe } from "./metaPixel";
import { initMetaPixel } from "./metaPixel";
import { getChannel, isTiktokChannel } from "./tiktokPixel";
import { initTiktokPixel } from "./tiktokPixel";
import { reportTiktokRegistration } from "./tiktokPixel";
import { reportTiktokPayment } from "./tiktokPixel";
import { reportTiktokSubscribe } from "./tiktokPixel";

// 像素上报统一入口（业务方统一从这里 import，不直接依赖具体像素）：
// channel 以 536_tk 开头 → TikTok 像素；否则 → Facebook 像素
// 函数签名与原 metaPixel 导出一致，整体 try/catch，上报异常不影响业务流程

// ---------- 注册上报（本地缓存一个状态对象统一处理）----------
// localStorage 存 {channel, registerdotnum, hasReport}：
// - 链接带渠道时更新状态：换渠道 → 新对象重新计；同渠道 → 更新 registerdotnum（hasReport 保留）
// - registerdotnum 为正整数：观看到第 N 集时上报（本次没看到，下次看到补报）
// - registerdotnum 无（0）：注册成功时直接上报
// - hasReport 为 true：该渠道已报过，不再报
// - 从没带过渠道（无状态对象）：不上报

const REG_STATE_KEY = "pixel_reg_state";
const REPORTED_CHANNELS_KEY = "pixel_reg_reported_channels";
const MAX_REPORTED_CHANNELS = 20;

// registerdotnum 解析：正整数有效，其余（缺失/非数字/≤0）返回 0
const parseDotNum = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : 0;
};

const readRegState = () => {
  try {
    const state = JSON.parse(localStorage.getItem(REG_STATE_KEY) || "null");
    return state && state.channel ? state : null;
  } catch (e) {
    return null;
  }
};

const writeRegState = (state) => {
  try {
    localStorage.setItem(REG_STATE_KEY, JSON.stringify(state));
  } catch (e) {}
};

// 已上报渠道列表：最多 20 个，满了去掉最早的再追加
// （状态对象换渠道会被覆盖，靠这个列表防止换走再换回来的渠道重复上报）
const readReportedChannels = () => {
  try {
    const list = JSON.parse(localStorage.getItem(REPORTED_CHANNELS_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
};

const isChannelReported = (channel) => readReportedChannels().indexOf(channel) !== -1;

const addReportedChannel = (channel) => {
  try {
    if (!channel) return;
    const list = readReportedChannels().filter((ch) => ch !== channel);
    list.push(channel);
    while (list.length > MAX_REPORTED_CHANNELS) {
      list.shift();
    }
    localStorage.setItem(REPORTED_CHANNELS_KEY, JSON.stringify(list));
  } catch (e) {}
};

// App 启动时调用：链接带渠道时更新本地注册上报状态
export function captureRegisterState(linkChannel, registerdotnum) {
  try {
    if (!linkChannel) return; // 链接没带渠道：保留现有状态（待补报的继续有效）
    const prev = readRegState();
    const dotNum = parseDotNum(registerdotnum);
    // 已报标记以已上报渠道列表为准（状态对象会被换渠道覆盖，列表不会）
    const reported = isChannelReported(linkChannel);
    let next;
    if (prev && prev.channel === linkChannel) {
      // 同渠道：更新指定集数（不带/无效则清零 → 走直接上报），已报标记与列表取或
      next = { ...prev, registerdotnum: dotNum, hasReport: prev.hasReport || reported };
    } else {
      // 换渠道：新对象，是否报过看列表
      next = { channel: linkChannel, registerdotnum: dotNum, hasReport: reported };
    }
    writeRegState(next);
    // 不带指定集数：进入即直接上报（该渠道未报过时）
    if (!next.hasReport && dotNum === 0) {
      fireRegistration();
    }
  } catch (e) {}
};

// 上报前校验：有状态、未报过（状态标记 + 渠道列表双重校验）、状态渠道和当前渠道一致
const getReportableRegState = () => {
  const state = readRegState();
  if (!state || state.hasReport) return null;
  if (!state.channel || state.channel !== getChannel()) return null;
  if (isChannelReported(state.channel)) return null;
  return state;
};

// 取本地用户 id（user_info.user_id），注册上报的 event_id 用
const getUserId = () => {
  try {
    const info = JSON.parse(localStorage.getItem("user_info") || "{}");
    return info && info.user_id ? String(info.user_id) : "";
  } catch (e) {
    return "";
  }
};

// 发送注册事件（按当前渠道分流 Lead / CompleteRegistration），状态标记 + 渠道列表都记上；
// event_id 用用户 id：本地暂无 user_info（新用户 init/getInfo 还没完成）时最多等 10s，
// 期间 user_info 到了就带上，超时仍没有就不带 event_id 直接报
const fireRegistration = () => {
  const state = readRegState();
  if (state) {
    writeRegState({ ...state, hasReport: true });
    addReportedChannel(state.channel);
  }
  const send = (userId) => {
    if (isTiktokChannel()) {
      reportTiktokRegistration(userId);
    } else {
      fbReportLead(userId);
    }
  };
  const userId = getUserId();
  if (userId) {
    send(userId);
    return;
  }
  let tries = 0;
  const timer = setInterval(() => {
    tries++;
    const uid = getUserId();
    if (uid || tries >= 20) {
      clearInterval(timer);
      send(uid);
    }
  }, 500);
};

// 注册成功时调用（mainservice 首次拿到 token）：
// 无状态/已报/渠道不一致不报；有指定集数先不报（等看到那集）；否则直接报
export function reportLead() {
  try {
    const state = getReportableRegState();
    if (!state) return;
    if (Number(state.registerdotnum) > 0) return;
    fireRegistration();
  } catch (e) {}
}

// 观看到某集时调用（watch 页）：有指定集数且看到第 N 集或更靠后、未报过、渠道一致 → 补报
export function reportRegistrationOnEpisode(dramaNum) {
  try {
    const state = getReportableRegState();
    if (!state) return;
    const dotNum = Number(state.registerdotnum);
    if (dotNum > 0 && Number(dramaNum) >= dotNum) {
      fireRegistration();
    }
  } catch (e) {}
}

// 应用启动时按渠道初始化像素（只加载对应脚本并发 Pageview）：TikTok 渠道 → TikTok 像素，其余 → Facebook 像素
export function initPixelByChannel() {
  try {
    if (isTiktokChannel()) {
      initTiktokPixel();
      return;
    }
    initMetaPixel();
  } catch (e) {}
}

// 支付完成（order_type != 2）：直接传档位对象，两侧各自从中取参数；orderId 为订单 id（两侧的 event_id 用）
export function reportPurchase(value, item, orderId) {
  try {
    if (isTiktokChannel()) {
      reportTiktokPayment(value, item, orderId);
      return;
    }
    fbReportPurchase(value, orderId);
  } catch (e) {}
}

// VIP 订阅支付成功（order_type = 2）：报 Subscribe；
// 直接传档位对象，两侧各自从中取参数；orderId 为订单 id（两侧的 event_id 用）
export function reportSubscribe(value, item, orderId) {
  try {
    if (isTiktokChannel()) {
      reportTiktokSubscribe(value, item, orderId);
      return;
    }
    fbReportSubscribe(value, orderId);
  } catch (e) {}
}
